import { usePageBackground } from "@/hooks/use-page-background"
import { useEffect, useState } from "react"

import { Check, StarIcon } from "@/icons"
import { Card, Heading, Icon, IconBadge, PageContainer, Points, Price, ProgressBar, SectionHeader, Text, type IconBadgeProps, type TextTone } from "@/components/ui"
import { getStoredCompany } from "@/lib/company-storage"
import { formatBRL } from "@/lib/format-brl"
import { formatPoints } from "@/lib/format-points"
import { fetchLoyalty, fetchLoyaltyTransactions } from "@/lib/loyalty-service"
import { LoyaltyPoints, LoyaltyProgram, LoyaltyTransaction } from "@/types/loyalty"
import { LoyaltyBenefitsSection } from "./LoyaltyBenefitsSection"

type TransactionTypeMeta = {
  badgeTone: NonNullable<IconBadgeProps["tone"]>
  amountTone: TextTone
  label: string
  /** Fixed sign prefix; when absent the sign is derived from the points amount. */
  sign?: "+" | "−"
}

/** Fallback for transaction types outside the typed contract (runtime safety). */
const FALLBACK_TRANSACTION_META: TransactionTypeMeta = {
  badgeTone: "neutral",
  amountTone: "muted",
  label: "Pontos do pedido",
}

const TRANSACTION_TYPE_META: Record<LoyaltyTransaction["transactionType"], TransactionTypeMeta> = {
  EARN: { badgeTone: "primary", amountTone: "secondary", label: "Pontos Ganhos", sign: "+" },
  REDEEM: { badgeTone: "warning", amountTone: "warning", label: "Pontos Resgatados", sign: "−" },
  EXPIRE: { badgeTone: "danger", amountTone: "danger", label: "Pontos Expirados", sign: "−" },
  ADJUSTMENT: { badgeTone: "neutral", amountTone: "muted", label: "Ajuste de Pontos" },
  REFUND: { badgeTone: "success", amountTone: "success", label: "Pontos Estornados", sign: "+" },
}

export function ClientLoyalty() {

  const [loyaltyProgram, setLoyaltyProgram] = useState<LoyaltyProgram>({
    id: '',
    merchantId: '',
    name: '',
    description: '',
    pointsPerCurrency: 0,
    minimumPointsToRedeem: 0,
    pointsValueInCurrency: 0,
    pointsExpirationDays: 0,
    active: false
  })
  const [loyaltyPoints, setLoyaltyPoints] = useState<LoyaltyPoints>({
    id: '',
    merchantId: '',
    pointsBalance: 0,
    createdAt: '',
    updatedAt: ''
  })
  const [loyaltyTransactions, setLoyaltyTransactions] = useState<LoyaltyTransaction[]>([])
  const [merchantId, setMerchantId] = useState("")

  const getLoyalty = async () => {
    const company = getStoredCompany()
    if (!company) return
    setMerchantId(company.id)

    const transactions = await fetchLoyaltyTransactions(company.id)
    setLoyaltyTransactions(transactions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()))

    const { points, program } = await fetchLoyalty(company.id)
    setLoyaltyProgram(program)
    setLoyaltyPoints(points)
  }

  useEffect(() => {
    getLoyalty()
  }, [])

  usePageBackground()

  const { minimumPointsToRedeem } = loyaltyProgram
  const progress = minimumPointsToRedeem > 0
    ? loyaltyPoints.pointsBalance / minimumPointsToRedeem
    : loyaltyPoints.pointsBalance > 0 ? 1 : 0

  const totalEarned = loyaltyTransactions
    .filter(transaction => transaction.transactionType === 'EARN')
    .reduce((total, transaction) => total + transaction.pointsAmount, 0)
  const totalSpent = Math.max(0,
    loyaltyTransactions
      .filter(transaction => transaction.transactionType === 'REDEEM')
      .reduce((total, transaction) => total + transaction.pointsAmount, 0)
    - loyaltyTransactions
      .filter(transaction => transaction.transactionType === 'REFUND')
      .reduce((total, transaction) => total + transaction.pointsAmount, 0)
  )

  return (
    <PageContainer top="header-gap" bottom="chrome" className="flex flex-col gap-6">
      <Heading level={1} className="sr-only">Fidelidade</Heading>

      <Card variant="hero" tone="primary" elevation="card" padding="2xl">
        <div className="mb-4 flex items-center gap-3">
          <Icon as={StarIcon} size="2xl" tone="on-primary" />
          <Heading level={2} size="page" tone="on-primary">Seus Pontos</Heading>
        </div>

        <div className="flex w-full items-center justify-between gap-4">
          <HeroStat label="Pontos Atuais" value={loyaltyPoints.pointsBalance} weight="bold" />
          <HeroStat label="Total Ganho" value={totalEarned} />
          <HeroStat label="Total Gasto" value={totalSpent} />
        </div>
      </Card>

      <Card padding="lg">
        <SectionHeader title={`É necessário ${formatPoints(minimumPointsToRedeem, "pontos")} para resgatar`} />

        <ProgressBar
          value={progress}
          size="lg"
          label="Progresso para resgatar pontos"
          className="mb-2"
        />
        <Text as="div" size="sm" tone="muted" className="flex justify-between">
          <Points value={loyaltyPoints.pointsBalance} unit="pts" />
          <Points value={minimumPointsToRedeem} unit="pts" />
        </Text>

        <Text size="sm" tone="muted" className="mt-3">
          Cada ponto vale <Price value={loyaltyProgram.pointsValueInCurrency} weight="semibold" />.
          Você tem <Points value={loyaltyPoints.pointsBalance} unit="pontos" weight="semibold" />
        </Text>
      </Card>

      <LoyaltyBenefitsSection merchantId={merchantId} />

      <section>
        <SectionHeader size="title" title="Histórico Recente" />
        <div className="flex flex-col gap-4">
          {loyaltyTransactions.slice(0, 10).map((transaction) => {
            const meta = TRANSACTION_TYPE_META[transaction.transactionType] ?? FALLBACK_TRANSACTION_META
            const sign = meta.sign ?? (transaction.pointsAmount > 0 ? '+' : '')

            return (
              <Card key={transaction.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IconBadge icon={StarIcon} tone={meta.badgeTone} size="lg" />
                  <div>
                    <Text weight="semibold" tone="default">{meta.label}</Text>

                    {transaction.orderId && (
                      <Text size="base" tone="subtle">
                        Pedido #{transaction.orderId.slice(0, 8)}
                      </Text>
                    )}

                    <Text size="sm" tone="muted">
                      {new Date(transaction.createdAt).toLocaleDateString('pt-BR')}
                    </Text>
                  </div>
                </div>
                <Text as="span" weight="bold" tone={meta.amountTone} align="right">
                  {sign}<Points value={Math.abs(transaction.pointsAmount)} unit="pts" />
                </Text>
              </Card>
            )
          })}
        </div>
      </section>

      <Card padding="lg">
        <SectionHeader title="Como Ganhar Pontos" spacing="sm" />
        <Text as="div" size="sm" tone="muted" className="flex items-center gap-2">
          <Icon as={Check} size="sm" tone="primary" />
          <span>Faça pedidos e ganhe {loyaltyProgram.pointsPerCurrency} {loyaltyProgram.pointsPerCurrency > 1 ? 'pontos' : 'ponto'} a cada {formatBRL(1)} gasto</span>
        </Text>
      </Card>
    </PageContainer>
  )
}

function HeroStat({ label, value, weight = "semibold" }: { label: string, value: number, weight?: "semibold" | "bold" }) {
  return (
    <div>
      <Text size="sm" tone="on-primary">{label}</Text>
      <Points value={value} size="3xl" weight={weight} tone="on-primary" className="block" />
    </div>
  )
}
